// @flow
import * as React from 'react'
import useStyles from './styles'
// Validation
import validate from 'validate.js'
import schema from './schema'
// Routing
import { useHistory } from 'react-router-dom'
// Auth
import { signIn, requiredResetPassword } from 'services/auth'
// Components
import LoginForm from './loginForm'
import ResetPasswordForm from './resetPasswordForm'

const defaultValues = {
  email: '',
  password: '',
  newPassword: '',
  confirmPassword: '',
}
const defaultTouched = {
  email: false,
  password: false,
  newPassword: false,
  confirmPassword: false,
}
const defaultErrors = {
  email: null,
  password: null,
  newPassword: null,
  confirmPassword: null,
}
declare type Props = {}
const Page = () => {
  const classes = useStyles()
  const history = useHistory()
  const [awsCognitoAuthChallenge, setAwsCognitoAuthChallenge] = React.useState(
    null
  )
  const [values, setValues] = React.useState(defaultValues)
  const [touched, setTouched] = React.useState(defaultTouched)
  const [errors, setErrors] = React.useState(defaultErrors)
  const [isValid, setIsValid] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [submitError, setSubmitError] = React.useState(null)

  const handleFieldChange = React.useCallback(
    (fieldName: string, value: string) => {
      setTouched({ ...touched, [fieldName]: true })
      setValues({ ...values, [fieldName]: value })
    },
    [touched, values]
  )
  const setDefaultState = React.useCallback(() => {
    console.log('setting default state')
    setValues(defaultValues)
    setTouched(defaultTouched)
    setErrors(defaultErrors)
    setIsValid(false)
    setIsLoading(false)
    setSubmitError(null)
  }, [])

  const validateForm = React.useCallback(() => {
    const newErrors: LoginErrors = {
      email: null,
      password: null,
      newPassword: null,
      confirmPassword: null,
    }
    let newIsValid = isValid

    if (!awsCognitoAuthChallenge) {
      const errors = validate(values, schema, { format: 'grouped' })
      if (errors) {
        if (errors.email) {
          newErrors.email = errors.email
        }
        if (errors.password) {
          newErrors.password = errors.password
        }
      }
      newIsValid = !newErrors.email && !newErrors.password
    } else {
      const newPasswordErrors = []
      if (!/[a-z]+/.test(values.newPassword)) {
        newPasswordErrors.push('must contain a lowercase letter')
      }
      if (!/[A-Z]+/.test(values.newPassword)) {
        newPasswordErrors.push('must contain an uppercase letter')
      }
      if (!/\d+/.test(values.newPassword)) {
        newPasswordErrors.push('must contain a number')
      }
      if (!/[\^$*.[\]{}()?|\-"!@#%&/,><':;|_~`]+/.test(values.newPassword)) {
        newPasswordErrors.push('must contain a special character')
      }
      if (values.newPassword.length < 8) {
        newPasswordErrors.push('must have at least 8 characters')
      }

      if (newPasswordErrors.length) {
        newErrors.newPassword = newPasswordErrors
      }

      if (values.confirmPassword !== values.newPassword) {
        newErrors.confirmPassword = ['Passwords do not match']
      }

      newIsValid = !newErrors.newPassword && !newErrors.confirmPassword
    }
    setErrors(newErrors)
    setIsValid(newIsValid)
  }, [isValid, awsCognitoAuthChallenge, values])

  const handleSignIn = React.useCallback(
    async (e) => {
      e.preventDefault()
      try {
        setIsLoading(true)
        setSubmitError(null)

        const result = await signIn(values.email, values.password)
        if (result) {
          setDefaultState()
          setValues(values)
          setAwsCognitoAuthChallenge(result)
          console.log(values)
          return
        }

        // Successful sign in, redirect to the dashboard
        history.replace('/')
      } catch (error) {
        setIsLoading(false)
        setSubmitError(error)
      }
    },
    [setDefaultState, values, history]
  )

  const handleResetPassword = React.useCallback(
    async (e) => {
      e.preventDefault()

      try {
        if (!awsCognitoAuthChallenge) {
          return
        }
        setIsLoading(true)
        setSubmitError(null)

        await requiredResetPassword(
          values.email,
          values.newPassword,
          awsCognitoAuthChallenge
        )
        // Successful sign in, redirect to the dashboard
        history.replace('/')
      } catch (error) {
        setIsLoading(false)
        setSubmitError(error)
      }
    },
    [values.email, awsCognitoAuthChallenge, values.newPassword, history]
  )
  React.useEffect(() => {
    validateForm()
  }, [values, validateForm])

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.contentBody}>
          {awsCognitoAuthChallenge ? (
            <ResetPasswordForm
              values={values}
              touched={touched}
              errors={errors}
              handleResetPassword={handleResetPassword}
              handleFieldChange={handleFieldChange}
              isValid={isValid}
              isLoading={isLoading}
              submitError={submitError}
            ></ResetPasswordForm>
          ) : (
            <LoginForm
              values={values}
              touched={touched}
              errors={errors}
              handleSignIn={handleSignIn}
              handleFieldChange={handleFieldChange}
              isValid={isValid}
              isLoading={isLoading}
              submitError={submitError}
            ></LoginForm>
          )}
        </div>
      </div>
    </div>
  )
}

export default Page
